import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useRouteContext } from '@tanstack/react-router';
import type { CampaignNumberPreviewQuery } from '@ooh/contracts';
import { offerQueries } from './queries';
import type { WithCallWithAuth } from './queries';

function useEffectiveAuth(): WithCallWithAuth {
  const { auth } = useRouteContext({ from: '__root__' });
  // RouterContext['auth'] is structurally identical to WithCallWithAuth but TS cannot
  // infer the narrowing from useRouteContext's return type — cast is safe, mirroring
  // mutations.ts's own useEffectiveAuth.
  return auth as WithCallWithAuth;
}

const CAMPAIGN_NUMBER_PREVIEW_DEBOUNCE_MS = 300;

/**
 * Debounces `input` the same way useDebouncedBbox debounces a map viewport
 * bbox (apps/web/src/hooks/use-debounced-bbox.ts): the first non-null value
 * is applied immediately (nothing was previewed before), and every value
 * after that is delayed, so rapidly changing client/brand/salesperson
 * selections on the creation form don't each fire their own preview request.
 */
function useDebouncedCampaignNumberPreviewInput(
  input: CampaignNumberPreviewQuery | null,
): CampaignNumberPreviewQuery | null {
  const [debounced, setDebounced] = React.useState<CampaignNumberPreviewQuery | null>(null);
  const initializedRef = React.useRef(false);

  React.useEffect(() => {
    if (input === null) {
      setDebounced(null);
      initializedRef.current = false;
      return;
    }
    if (!initializedRef.current) {
      initializedRef.current = true;
      setDebounced(input);
      return;
    }
    const id = setTimeout(() => setDebounced(input), CAMPAIGN_NUMBER_PREVIEW_DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [input]);

  return debounced;
}

const EMPTY_CAMPAIGN_NUMBER_PREVIEW_QUERY: CampaignNumberPreviewQuery = {
  clientId: 0,
  acronym: '',
  salespersonName: '',
};

/**
 * Live preview of the campaign number a draft would receive if created right
 * now (offer-draft-numbering / "Campaign-number preview on offer creation
 * forms"). `input` is null until the form has a client, an acronym and a
 * salesperson name to compose with — the query stays disabled until then,
 * and debounces further changes (see useDebouncedCampaignNumberPreviewInput
 * above).
 *
 * Shared by both /offers/new (create-offer-page.tsx, which calls this
 * directly) and the quick-add offer picker's inline "creating" mode form —
 * QuickAddOfferPicker never calls this itself (no router/query context in
 * apps/mockups), so its caller calls it instead and passes the result down;
 * see quick-add-offer-picker.tsx's `campaignNumberPreview` prop doc comment.
 */
export function useCampaignNumberPreview(input: CampaignNumberPreviewQuery | null) {
  const auth = useEffectiveAuth();
  const debouncedInput = useDebouncedCampaignNumberPreviewInput(input);

  return useQuery({
    ...offerQueries.campaignNumberPreview(
      auth,
      debouncedInput ?? EMPTY_CAMPAIGN_NUMBER_PREVIEW_QUERY,
    ),
    enabled: debouncedInput !== null,
  });
}
